// src/pages/tournament/RoundsPanel.jsx
import React, { useState } from 'react';
import { useConfirm } from '../../components/ConfirmDialog';
import { ChevronIcon, PlusIcon, TrashIcon } from '../../components/icons';
import MatchupCard from './MatchupCard';
import RequestsEditor from './RequestsEditor';
import { describeNextRound } from '../../utils/planShape';
import { roundTitle } from '../../utils/groups';

export const MIN_PLAYERS_FOR_ROUND = 4; // two teams of two
export const MIN_TEAMS_FOR_ROUND = 2; // fixed-teams format: two teams to face each other

// A matchup nobody has scored yet: no match has a set with any points in it. A cancelled matchup is not
// in the way; it counts as a break, not a game left to score.
const isUnready = (matchup) =>
    !matchup.cancelled && (matchup.matches || []).every((match) => (match.sets || []).every((set) => set.team1Score + set.team2Score === 0));

function RoundsPanel({ rounds, activePlayerCount, players = [], fixedTeams = [], byTeam = false, grouped = false, details = {},
                       loading, actions, locked, onGoToPlayers }) {
    const confirm = useConfirm();
    const [expanded, setExpanded] = useState({});
    const [requests, setRequests] = useState([]); // pairings asked for the next round

    // The newest round is open unless the user chose otherwise.
    const latestRoundId = rounds.length > 0 ? rounds[rounds.length - 1].roundId : null;
    const isOpen = (roundId) => expanded[roundId] ?? roundId === latestRoundId;
    const toggle = (roundId) => setExpanded((prev) => ({ ...prev, [roundId]: !isOpen(roundId) }));

    const latest = rounds.length > 0 ? rounds[rounds.length - 1] : null;
    const unreadyMatchup = latest ? latest.matchups.find(isUnready) : null;
    // A fixed team can only play once both of its players are active.
    const activeIds = new Set(players.filter((player) => player.active !== false).map((player) => player.playerId));
    const activeTeamCount = fixedTeams.filter((team) => team.players.every((player) => activeIds.has(player.playerId))).length;
    const enoughToPlay = byTeam ? activeTeamCount >= MIN_TEAMS_FOR_ROUND : activePlayerCount >= MIN_PLAYERS_FOR_ROUND;
    const finalPlayed = grouped && details.nextRoundTitle === null;
    const canCreateRound = enoughToPlay && !locked && !unreadyMatchup && !finalPlayed;
    const singlesEnabled = Boolean(details.singlesEnabled);
    const nextRound = byTeam ? null : describeNextRound(activePlayerCount, details.courts, singlesEnabled);

    const handleAddRound = async () => {
        const round = await actions.addRound(byTeam ? undefined : requests);
        if (round) {
            // the new round is open and every other one closed, so it is the one to be seen
            setExpanded({ ...Object.fromEntries(rounds.map((other) => [other.roundId, false])), [round.roundId]: true });
            setRequests([]); // (they were for this round only)
        }
    };

    const handleDeleteRound = async (round) => {
        const confirmed = await confirm({
            title: `Delete ${roundTitle(round)}?`,
            message: 'Its matchups, matches and scores will be removed.',
            confirmLabel: 'Delete round',
        });
        if (confirmed) actions.deleteRound(round.roundId);
    };

    return (
        <section aria-labelledby="rounds-heading">
            <div className="rounds-toolbar">
                <div>
                    <h2 id="rounds-heading">Rounds</h2>
                    {!loading && locked && <p className="hint">This tournament is finished, so no new rounds can be created.</p>}
                    {!loading && !locked && !enoughToPlay && (
                        <p className="hint">
                            {byTeam
                                ? `Make at least ${MIN_TEAMS_FOR_ROUND} active teams to create a round.`
                                : `Add at least ${MIN_PLAYERS_FOR_ROUND} active players to create a round.`}
                            {onGoToPlayers && (
                                <>
                                    {' '}
                                    <button className="link-button" onClick={onGoToPlayers}>
                                        Go to the players
                                    </button>
                                </>
                            )}
                        </p>
                    )}
                    {!loading && !locked && finalPlayed && <p className="hint">The final has been played: the tournament is complete.</p>}
                    {!loading && !locked && canCreateRound && nextRound && <p className="hint">Next round: {nextRound}.</p>}
                    {!loading && !locked && enoughToPlay && unreadyMatchup && (
                        <p className="hint">
                            {roundTitle(latest)} has a matchup with no score yet. Score it or cancel it to
                            create the next round.
                        </p>
                    )}
                </div>
                <button className="btn btn-primary" onClick={handleAddRound} disabled={!canCreateRound}>
                    <PlusIcon /> {grouped && details.nextRoundTitle ? `Plan ${details.nextRoundTitle}` : 'New round'}
                </button>
            </div>

            {!byTeam && !locked && !loading && players.length > 0 && (
                <RequestsEditor players={players} requests={requests} onChange={setRequests} courts={details.courts} />
            )}

            {loading ? (
                <div className="skeleton" aria-busy="true" />
            ) : rounds.length === 0 ? (
                <div className="empty">
                    <strong>No rounds yet</strong>
                    <p>A round pairs everyone into teams and matchups automatically.</p>
                </div>
            ) : (
                <div className="rounds">
                    {rounds.length > 1 && (
                        <p className="hint">Scores of earlier rounds can still be corrected. Pairings already planned do not change.</p>
                    )}
                    {/* newest first, so the round being played is at the top */}
                    {[...rounds].reverse().map((round) => {
                        const open = isOpen(round.roundId);
                        return (
                            <article key={round.roundId} className="card">
                                <div className="round-head">
                                    <button
                                        className="section-toggle"
                                        aria-expanded={open}
                                        aria-label={roundTitle(round)}
                                        onClick={() => toggle(round.roundId)}
                                    >
                                        <ChevronIcon open={open} />
                                        <h3>{roundTitle(round)}</h3>
                                        <span className="badge">
                                            {round.matchups.length} matchup{round.matchups.length === 1 ? '' : 's'}
                                            {round.matchups.some((matchup) => matchup.isSingles) && ' · incl. singles'}
                                        </span>
                                    </button>
                                    {!locked && round.roundId === latestRoundId && (
                                        <button
                                            className="btn btn-ghost btn-icon btn-icon-danger"
                                            aria-label={`Delete ${roundTitle(round).toLowerCase()}`}
                                            onClick={() => handleDeleteRound(round)}
                                        >
                                            <TrashIcon />
                                        </button>
                                    )}
                                </div>
                                {open && (
                                    <div className="round-body">
                                        {(round.idlePlayers || []).length > 0 && (
                                            <p className="sitting-out">
                                                <span className="label">Sitting out</span>
                                                {round.idlePlayers.map((player) => (
                                                    <span key={player.playerId} className="badge">
                                                        {player.firstName} {player.lastName}
                                                    </span>
                                                ))}
                                            </p>
                                        )}
                                        {round.matchups.length === 0 ? (
                                            <p className="hint">This round has no matchups.</p>
                                        ) : (
                                            round.matchups.map((matchup, index) => (
                                                <MatchupCard
                                                    key={matchup.matchupId}
                                                    matchup={matchup}
                                                    index={index}
                                                    courtNumber={details.courts && round.stage !== 'knockout' ? index + 1 : null}
                                                    roundId={round.roundId}
                                                    actions={actions}
                                                    locked={locked}
                                                    canCancel={round.roundId === latestRoundId}
                                                />
                                            ))
                                        )}
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

export default RoundsPanel;
